import * as Yup from 'yup';
import React, { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, UseFormReset } from 'react-hook-form';
import { FormProvider, RHFTextField } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import { Box, Dialog, Button, DialogTitle, DialogContent, DialogActions } from '@mui/material';

interface EditFavouritesNameDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string }, reset: UseFormReset<{ name: string }>) => void;
  initialName: string;
  title?: string;
}

const NameSchema = Yup.object().shape({
  name: Yup.string()
    .max(100, 'Favourites list name must be shorter than 100 characters')
    .required('Favourites list name is required'),
});

const EditFavouritesNameDialog: React.FC<EditFavouritesNameDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialName,
  title = 'Edit Favourites List Name',
}) => {
  const defaultValues = React.useMemo(
    () => ({
      name: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(NameSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
    setValue,
  } = methods;

  useEffect(() => {
    if (open && initialName) {
      setValue('name', initialName, { shouldDirty: false });
    }
  }, [open, initialName, setValue]);

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);

  const handleFormSubmit = handleSubmit((data) => onSubmit(data, reset));

  return (
    <FormProvider methods={methods} onSubmit={handleFormSubmit}>
      <Dialog open={open} onClose={onClose} aria-labelledby="edit-favourites-dialog-title">
        <DialogTitle id="edit-favourites-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <RHFTextField
              name="name"
              label="Favourites List Name"
              fullWidth
              aria-required="true"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isSubmitting || !isDirty}
            color="primary"
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default EditFavouritesNameDialog;
